import { Link } from "@tanstack/react-router";
import { parseISO, calendarDatePath, dayOfWeek, DAY_NAMES_SHORT_ID } from "@/lib/calendar/dates";
import type { Holiday } from "@/data/holidays";
import type { HariPenting } from "@/data/hari-penting";
import { useLocale } from "@/i18n/LocaleContext";
import { translateHolidayName, translateHariPenting } from "@/i18n/holidays";

interface Props {
  year: number;
  month: number;
  holidays: Holiday[];
  hariPenting: HariPenting[];
}

export function MonthLegendList({ year, month, holidays, hariPenting }: Props) {
  const { t, href, locale } = useLocale();
  const libur = holidays.filter((h) => h.type === "libur");
  const cuti = holidays.filter((h) => h.type === "cuti-bersama");

  if (libur.length === 0 && cuti.length === 0 && hariPenting.length === 0) return null;

  const daysShort = locale === "en"
    ? ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"]
    : DAY_NAMES_SHORT_ID;

  function row(dateISO: string, color: "red" | "blue" | "gray", label: string) {
    const p = parseISO(dateISO)!;
    const dow = dayOfWeek(p.y, p.m, p.d);
    const dotClass =
      color === "red" ? "bg-red-date" : color === "blue" ? "bg-cuti-bersama" : "bg-hari-penting";
    const txtClass =
      color === "red" ? "text-red-date font-medium" : color === "blue" ? "text-cuti-bersama font-medium" : "text-foreground";
    return (
      <li key={`${dateISO}-${label}`} className="flex items-start gap-2 text-sm">
        <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${dotClass}`} />
        <Link to={href(calendarDatePath(p.y, p.m, p.d)) as never} className="hover:text-primary">
          <span className="text-muted-foreground">{daysShort[dow]}, </span>
          <span className={txtClass}>
            {p.d} {t.common.months[p.m - 1]}
          </span>
          <span className="text-muted-foreground"> - </span>
          <span>{label}</span>
        </Link>
      </li>
    );
  }

  return (
    <section className="rounded-lg border border-border bg-card p-4">
      <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        {t.common.legendCaption} - {t.common.months[month - 1]} {year}
      </h3>
      <ul className="grid gap-1.5 sm:grid-cols-2">
        {libur.map((h) => row(h.date, "red", translateHolidayName(h.name, locale)))}
        {cuti.map((h) => row(h.date, "blue", translateHolidayName(h.name, locale)))}
        {hariPenting.map((h) => {
          const [mm, dd] = h.monthDay.split("-").map((n) => parseInt(n, 10));
          const iso = `${year}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`;
          return row(iso, "gray", translateHariPenting(h.name, locale));
        })}
      </ul>
    </section>
  );
}